import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, FileAudio, Loader2, Mic, Play, Search, Trash2, X } from 'lucide-react';
import {
  leggiTrascrizione, RiepilogoAudio, TrascrizioneAudio, urlAssolutoAudio,
} from '../../services/verbaliAudioService';
import {
  cercaSegmenti, formattaMs, partiEvidenziate, segmentoAlTempo, useUrlStabile,
} from '../../utils/verbaliTrascrizione';
import { nomeAudio, testoAudioEliminato, TESTO_SENZA_VOCI, TESTO_TRASCRIZIONE_PARZIALE } from './testiAudio';

interface VerbaleTrascrizioneProps {
  eventId: string;
  audio: RiepilogoAudio[];
  /** Dall'esterno (es. «Ascolta da 1:02:05» sul foglio): scorre al segmento e lo riproduce. */
  tempo?: { audioId: string; ms: number } | null;
  /**
   * Cambia dopo il salvataggio delle voci (es. un contatore della pagina): la trascrizione si rilegge
   * con i nomi nuovi, mostrando intanto quella di prima.
   */
  versioneVoci?: number | string;
}

/** Audio con una trascrizione da leggere (anche parziale, con l'audio scaduto a metà). */
export const audioConTrascrizione = (audio: RiepilogoAudio[]) =>
  [...audio]
    .filter((a) => a.stato === 'trascritta' || !!a.trascrizioneParziale)
    .sort((a, b) => (a.ordine || 0) - (b.ordine || 0));

type Stato = { tipo: 'carica' } | { tipo: 'pronta'; dati: TrascrizioneAudio } | { tipo: 'errore'; messaggio: string };

const chiaveAudio = (a: RiepilogoAudio, versioneVoci: number | string) =>
  `${a._id}|${a.stato}|${a.trascrizioneParziale ? 1 : 0}|${versioneVoci}`;

const TestoEvidenziato: React.FC<{ testo: string; q: string }> = ({ testo, q }) => (
  <>
    {partiEvidenziate(testo, q).map((p, i) => (p.trovato
      ? <mark key={i} className="rounded bg-amber-200 px-0.5 text-inherit dark:bg-amber-500/40">{p.testo}</mark>
      : <React.Fragment key={i}>{p.testo}</React.Fragment>))}
  </>
);

/**
 * Scheda «Trascrizione»: un audio alla volta, ricerca con evidenziazione, clic su un segmento per
 * ascoltarlo da lì. Con l'audio eliminato resta il testo, senza lettore.
 */
const VerbaleTrascrizione: React.FC<VerbaleTrascrizioneProps> = ({ eventId, audio, tempo, versioneVoci = 0 }) => {
  const leggibili = useMemo(() => audioConTrascrizione(audio), [audio]);
  const [sceltoId, setSceltoId] = useState<string | null>(null);
  const [stati, setStati] = useState<Record<string, Stato>>({});
  const [q, setQ] = useState('');
  const [attivo, setAttivo] = useState(-1);
  const [lettoreGuasto, setLettoreGuasto] = useState(false);
  const [tentativo, setTentativo] = useState(0);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const righeRef = useRef<Map<number, HTMLLIElement>>(new Map());
  /** Tempo arrivato dall'esterno, applicato quando la trascrizione di quell'audio è pronta. */
  const tempoInSospesoRef = useRef<{ audioId: string; ms: number } | null>(null);
  /** Ultimo `tempo` ricevuto: si applica una sola volta per riferimento, non a ogni elenco riletto. */
  const ultimoTempoRef = useRef<{ audioId: string; ms: number } | null | undefined>(undefined);
  /** Ultima trascrizione pronta per audio: resta visibile mentre si rilegge (voci rinominate). */
  const ultimiDatiRef = useRef<Map<string, TrascrizioneAudio>>(new Map());
  const [scorriA, setScorriA] = useState<number | null>(null);

  const scelto = leggibili.find((a) => a._id === sceltoId) || leggibili[0] || null;
  const chiave = scelto ? chiaveAudio(scelto, versioneVoci) : null;
  const stato = chiave ? stati[chiave] : undefined;
  if (scelto && stato?.tipo === 'pronta') ultimiDatiRef.current.set(scelto._id, stato.dati);
  const dati = stato?.tipo === 'pronta'
    ? stato.dati
    : (scelto && stato?.tipo !== 'errore' ? ultimiDatiRef.current.get(scelto._id) || null : null);
  const sceltoIdEffettivo = scelto?._id ?? null;
  // Il primo URL firmato resta finché funziona: la pagina rilegge l'elenco (URL nuovi) mentre si ascolta.
  const [urlAscolto, rinnovaUrl] = useUrlStabile(
    scelto && !scelto.eliminatoAt ? scelto._id : null,
    scelto && !scelto.eliminatoAt ? urlAssolutoAudio(scelto.urlAscolto) : null
  );

  // Trascrizione dell'audio scelto (una volta per stato dell'audio).
  useEffect(() => {
    if (!scelto || !chiave || stati[chiave]) return undefined;
    let vivo = true;
    setStati((s) => ({ ...s, [chiave]: { tipo: 'carica' } }));
    leggiTrascrizione(eventId, scelto._id)
      .then((t) => { if (vivo) setStati((s) => ({ ...s, [chiave]: { tipo: 'pronta', dati: t } })); })
      .catch((err: any) => {
        if (!vivo) return;
        const messaggio = err?.response?.data?.error || 'Trascrizione non caricata: riprova';
        setStati((s) => ({ ...s, [chiave]: { tipo: 'errore', messaggio } }));
      });
    return () => {
      vivo = false;
      // Interrotta prima della risposta: al prossimo giro si ricarica.
      setStati((s) => (s[chiave]?.tipo === 'carica' ? (({ [chiave]: _tolto, ...resto }) => resto)(s) : s));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, chiave, tentativo]);

  useEffect(() => {
    setAttivo(-1);
    setLettoreGuasto(false);
  }, [scelto?._id]);

  const riproduci = useCallback((ms: number) => {
    const el = audioRef.current;
    if (!el) return;
    try {
      el.currentTime = Math.max(0, ms) / 1000;
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => undefined);
    } catch { /* lettore non pronto */ }
  }, []);

  // Tempo dall'esterno: si arma solo quando cambia il riferimento di `tempo` (l'elenco degli audio
  // riletto ogni 10 s non deve riportare il lettore al punto chiesto né togliere la ricerca).
  // Si sceglie l'audio appena è leggibile, poi (a trascrizione pronta) scorre e riproduce.
  useEffect(() => {
    if (tempo !== ultimoTempoRef.current) {
      ultimoTempoRef.current = tempo;
      tempoInSospesoRef.current = tempo || null;
    }
    const t = tempoInSospesoRef.current;
    if (t && leggibili.some((a) => a._id === t.audioId)) setSceltoId(t.audioId);
  }, [tempo, leggibili]);

  useEffect(() => {
    const t = tempoInSospesoRef.current;
    if (!t || !dati || sceltoIdEffettivo !== t.audioId) return;
    tempoInSospesoRef.current = null;
    setQ('');
    const indice = segmentoAlTempo(dati.segmenti, t.ms);
    setAttivo(indice);
    setScorriA(indice);
    riproduci(t.ms);
  }, [dati, sceltoIdEffettivo, tempo, riproduci]);

  // Scorrimento dopo che la riga è stata disegnata (anche dopo aver tolto la ricerca).
  useEffect(() => {
    if (scorriA === null) return;
    const riga = righeRef.current.get(scorriA);
    if (riga && typeof riga.scrollIntoView === 'function') riga.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setScorriA(null);
  }, [scorriA, q]);

  const visibili = useMemo(() => (dati ? cercaSegmenti(dati.segmenti, q) : []), [dati, q]);
  const ricercaAttiva = q.trim().length >= 2;

  const alTempo = () => {
    const el = audioRef.current;
    if (!el || !dati || el.paused) return;
    const indice = segmentoAlTempo(dati.segmenti, el.currentTime * 1000);
    if (indice !== attivo) setAttivo(indice);
  };

  if (!leggibili.length) {
    return (
      <p className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
        La trascrizione compare qui quando un audio è stato trascritto.
      </p>
    );
  }

  const parziale = !!(dati?.parziale || scelto?.trascrizioneParziale);

  return (
    <section aria-label="Trascrizione" className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      {leggibili.length > 1 && (
        <div role="tablist" aria-label="Audio" className="flex gap-1 overflow-x-auto border-b border-slate-200 px-3 pt-3 dark:border-gray-700">
          {leggibili.map((a) => {
            const selezionato = a._id === scelto?._id;
            return (
              <button
                key={a._id}
                type="button"
                role="tab"
                aria-selected={selezionato}
                onClick={() => { setSceltoId(a._id); setQ(''); }}
                className={`inline-flex max-w-[12rem] items-center gap-1.5 truncate rounded-t-lg border-b-2 px-3 py-1.5 text-sm font-medium ${
                  selezionato
                    ? 'border-emerald-600 text-emerald-700 dark:border-emerald-400 dark:text-emerald-300'
                    : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {a.origine === 'file'
                  ? <FileAudio className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                  : <Mic className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />}
                <span className="truncate">{nomeAudio(a)}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="space-y-2 p-3">
        {urlAscolto && !lettoreGuasto ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <audio
            ref={audioRef}
            controls
            preload="metadata"
            src={urlAscolto}
            onTimeUpdate={alTempo}
            onError={() => { if (!rinnovaUrl()) setLettoreGuasto(true); }}
            className="h-9 w-full"
          />
        ) : null}
        {urlAscolto && lettoreGuasto && (
          <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
            L'audio non si carica: il collegamento potrebbe essere scaduto, ricarica la pagina.
          </p>
        )}
        {scelto?.eliminatoAt && (
          <p className="flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-gray-700/50 dark:text-gray-300">
            <Trash2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
            {testoAudioEliminato(scelto.eliminatoAt)}
          </p>
        )}
        {parziale && (
          <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
            {TESTO_TRASCRIZIONE_PARZIALE}
          </p>
        )}
        {dati?.senzaVoci && (
          <p className="text-xs text-slate-500 dark:text-gray-400">{TESTO_SENZA_VOCI}.</p>
        )}

        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca una parola…"
            aria-label="Cerca nella trascrizione"
            className="w-full rounded-lg border border-slate-300 bg-white py-1.5 pl-8 pr-8 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ('')}
              aria-label="Cancella la ricerca"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-700 dark:hover:text-gray-200"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </div>
        {ricercaAttiva && dati && (
          <p className="text-xs text-slate-500 dark:text-gray-400" aria-live="polite">
            {visibili.length === 0 ? 'Nessun risultato' : visibili.length === 1 ? '1 risultato' : `${visibili.length} risultati`}
          </p>
        )}
      </div>

      <div className="max-h-[28rem] overflow-y-auto border-t border-slate-100 px-3 pb-3 dark:border-gray-700">
        {(!stato || stato.tipo === 'carica') && !dati && (
          <p className="flex items-center gap-2 py-4 text-sm text-slate-500 dark:text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Caricamento della trascrizione…
          </p>
        )}
        {stato?.tipo === 'errore' && chiave && (
          <p className="flex flex-wrap items-center gap-2 py-4 text-sm text-red-600 dark:text-red-400">
            {stato.messaggio}
            <button
              type="button"
              onClick={() => {
                setStati(({ [chiave]: _tolto, ...resto }) => resto);
                setTentativo((n) => n + 1);
              }}
              className="text-xs font-semibold text-emerald-700 hover:underline dark:text-emerald-300"
            >
              Riprova
            </button>
          </p>
        )}
        {dati && dati.segmenti.length === 0 && (
          <p className="py-4 text-sm text-slate-500 dark:text-gray-400">La trascrizione è vuota: nell'audio non si sente parlare.</p>
        )}
        {dati && (
          <ol className="divide-y divide-slate-100 dark:divide-gray-700/70">
            {visibili.map((i) => {
              const s = dati.segmenti[i];
              const corrente = i === attivo;
              return (
                <li
                  key={i}
                  data-segmento=""
                  aria-current={corrente ? 'true' : undefined}
                  ref={(el) => { if (el) righeRef.current.set(i, el); else righeRef.current.delete(i); }}
                  className={`-mx-1.5 rounded-lg px-1.5 py-2 ${corrente ? 'bg-emerald-50 dark:bg-emerald-900/25' : ''}`}
                >
                  <button
                    type="button"
                    onClick={() => { setAttivo(i); riproduci(s.daMs); }}
                    disabled={!urlAscolto || lettoreGuasto}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline disabled:cursor-default disabled:text-slate-500 disabled:no-underline dark:text-emerald-300 dark:disabled:text-gray-400"
                  >
                    {urlAscolto && !lettoreGuasto && <Play className="h-3 w-3" aria-hidden="true" />}
                    <span className="tabular-nums">{formattaMs(s.daMs)}</span>
                    {s.nome ? <span className={s.certa ? '' : 'italic'}>{` · ${s.nome}`}</span> : null}
                  </button>
                  <p className="mt-0.5 text-sm leading-relaxed text-slate-800 dark:text-gray-200">
                    <TestoEvidenziato testo={s.testo} q={q} />
                  </p>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </section>
  );
};

export default VerbaleTrascrizione;
